/**
 * Landing page runtime config contract (consumer).
 * Mirrored in RRAIMVP3/src/shared/types/landing-config.ts — keep both in sync.
 *
 * Fetched once on mount via Firestore getDoc (landing project).
 * Never polls and never wakes Cloud Run — scale-to-zero friendly.
 * GET /v1/landing-config remains for admin/debug/live-verify only.
 */

export const LANDING_VARIANTS = ["waitlist", "beta", "launch"] as const;
export type LandingVariant = (typeof LANDING_VARIANTS)[number];

export type LocalizedText =
  | string
  | {
      default: string;
      translations?: Partial<Record<string, string>>;
    };

export interface LandingSectionsConfig {
  features: boolean;
  pricing: boolean;
  partners: boolean;
}

export interface LandingHeroCopy {
  headline?: LocalizedText;
  subhead?: LocalizedText;
  cta?: LocalizedText;
}

export interface LandingPricingTierSnapshot {
  id: string;
  name: string;
  priceEurMonthly: number;
  creditsPerMonth: number;
  highlight?: boolean;
}

export interface LandingPricingSnapshot {
  tiers: LandingPricingTierSnapshot[];
  fetchedAt: string;
}

export interface LandingConfigSchedule {
  activateAt: string;
  variant: LandingVariant;
  revertAt?: string;
  nextVariant?: LandingVariant;
}

export interface LandingConfigSource {
  kind: "manual" | "launch_plan" | "default";
  planId?: string;
  strategyId?: string;
  previousConfig?: LandingConfigDocument | null;
}

export interface StoreAvailability {
  android: string[];
  ios: string[];
}

export const FOUNDER_OFFER_METRICS = [
  "new_referrals",
  "planned_outfits",
  "confirmed_items",
] as const;

export type FounderOfferMetric = (typeof FOUNDER_OFFER_METRICS)[number];

export interface FounderOfferRequirement {
  metric: FounderOfferMetric;
  target: number;
}

/**
 * Optional block on `{prefix}_landing_config/active`.
 * Read inside the existing one-shot getDoc. Never call the main API from the landing.
 * Mirrored by `LandingFounderOffer` in src/shared/types/landing-config.ts.
 */
export interface LandingFounderOffer {
  enabled: boolean;
  programId: string;
  cap: number;
  slotsLeft: number;
  startsAt: string;
  deadline: string;
  requirements: FounderOfferRequirement[];
  termsPath: string;
}

export interface LandingConfigDocument {
  variant: LandingVariant;
  sections: LandingSectionsConfig;
  hero: LandingHeroCopy;
  utmCampaignKey?: string;
  pricingSnapshot: LandingPricingSnapshot | null;
  schedule: LandingConfigSchedule | null;
  source: LandingConfigSource;
  storeAvailability: StoreAvailability;
  /** Present while the founding-member campaign is published to the homepage. */
  founderOffer?: LandingFounderOffer;
  updatedBy?: string;
  updatedAt: string;
}

export const DEFAULT_LANDING_SECTIONS: LandingSectionsConfig = {
  features: false,
  pricing: false,
  partners: true,
};

/** Spain Play live, Apple waitlist. RU stays off this default. */
export const DEFAULT_STORE_AVAILABILITY: StoreAvailability = {
  android: ["ES"],
  ios: [],
};

/**
 * Failure-only fallback = store CTAs + partners.
 * Used when Firestore strategy fetch fails / is missing — not for first paint.
 */
export const DEFAULT_LANDING_CONFIG: LandingConfigDocument = {
  variant: "launch",
  sections: { ...DEFAULT_LANDING_SECTIONS },
  hero: {},
  utmCampaignKey: undefined,
  pricingSnapshot: null,
  schedule: null,
  source: { kind: "default" },
  storeAvailability: {
    android: [...DEFAULT_STORE_AVAILABILITY.android],
    ios: [...DEFAULT_STORE_AVAILABILITY.ios],
  },
  updatedAt: new Date(0).toISOString(),
};

/**
 * Public-site bootstrap shown on SSR / first paint while strategy loads.
 * Waitlist and beta homepage variants are retired — store CTAs only.
 */
export const STRATEGY_BOOTSTRAP_LANDING_CONFIG: LandingConfigDocument = {
  variant: "launch",
  sections: { ...DEFAULT_LANDING_SECTIONS },
  hero: {},
  utmCampaignKey: "es_market_test_2026_08",
  pricingSnapshot: null,
  schedule: null,
  source: { kind: "manual" },
  storeAvailability: {
    android: [...DEFAULT_STORE_AVAILABILITY.android],
    ios: [...DEFAULT_STORE_AVAILABILITY.ios],
  },
  updatedAt: new Date(0).toISOString(),
};

/** Public website never shows waitlist/beta — store links only. */
export function toPublicLandingConfig(
  config: LandingConfigDocument,
): LandingConfigDocument {
  const availability = config.storeAvailability ?? DEFAULT_STORE_AVAILABILITY;
  const withAvailability = {
    ...config,
    sections: { ...config.sections },
    hero: { ...config.hero },
    storeAvailability: {
      android: [...availability.android],
      ios: [...availability.ios],
    },
  };
  if (config.variant === "launch") {
    return withAvailability;
  }
  return {
    ...withAvailability,
    variant: "launch",
    sections: { ...DEFAULT_LANDING_SECTIONS },
    hero: {},
  };
}

export type LandingConfigFetchResult =
  | { status: "ok"; config: LandingConfigDocument }
  | { status: "fallback"; config: LandingConfigDocument }
  | { status: "aborted"; config: LandingConfigDocument };

const DEFAULT_FOUNDER_TERMS_PATH = "/promociones/fundador";

function positiveInt(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.floor(n);
}

function nonNegativeInt(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

export function normalizeFounderOffer(raw: unknown): LandingFounderOffer | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const data = raw as Partial<LandingFounderOffer>;
  const programId = typeof data.programId === "string" ? data.programId.trim() : "";
  const cap = positiveInt(data.cap);
  const slotsLeft = nonNegativeInt(data.slotsLeft);
  const startsAt = typeof data.startsAt === "string" ? data.startsAt.trim() : "";
  const deadline = typeof data.deadline === "string" ? data.deadline.trim() : "";
  const requirements = Array.isArray(data.requirements)
    ? data.requirements.flatMap((row) => {
        if (!row || typeof row !== "object") return [];
        const metric = (row as { metric?: unknown }).metric;
        const target = positiveInt((row as { target?: unknown }).target);
        if (
          typeof metric !== "string" ||
          !(FOUNDER_OFFER_METRICS as readonly string[]).includes(metric) ||
          target === null
        ) {
          return [];
        }
        return [{ metric: metric as FounderOfferMetric, target }];
      })
    : [];
  if (!programId || cap === null || slotsLeft === null || !startsAt || !deadline || requirements.length === 0) {
    return undefined;
  }
  let termsPath = DEFAULT_FOUNDER_TERMS_PATH;
  if (typeof data.termsPath === "string") {
    const path = data.termsPath.trim();
    if (path.startsWith("/") && !path.startsWith("//")) termsPath = path;
  }
  return {
    enabled: data.enabled === true,
    programId,
    cap,
    slotsLeft: Math.min(slotsLeft, cap),
    startsAt,
    deadline,
    requirements,
    termsPath,
  };
}

export function isLandingVariant(value: unknown): value is LandingVariant {
  return (
    typeof value === "string" &&
    (LANDING_VARIANTS as readonly string[]).includes(value)
  );
}

export function resolveLocalizedText(
  value: LocalizedText | undefined,
  locale: string,
): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.translations?.[locale] ?? value.default ?? "";
}

/**
 * Resolve schedule lazily at read time (no cron).
 * Always returns a shallow clone so callers can keep both raw + resolved.
 */
export function resolveLandingConfigSchedule(
  config: LandingConfigDocument,
  now: Date = new Date(),
): LandingConfigDocument {
  const clone = (): LandingConfigDocument => {
    const availability = config.storeAvailability ?? DEFAULT_STORE_AVAILABILITY;
    return {
      ...config,
      sections: { ...config.sections },
      hero: { ...config.hero },
      storeAvailability: {
        android: [...availability.android],
        ios: [...availability.ios],
      },
    };
  };

  const schedule = config.schedule;
  if (!schedule?.activateAt) return clone();

  const activateMs = Date.parse(schedule.activateAt);
  if (Number.isNaN(activateMs)) return clone();

  const nowMs = now.getTime();
  if (nowMs < activateMs) return clone();

  if (schedule.revertAt) {
    const revertMs = Date.parse(schedule.revertAt);
    if (!Number.isNaN(revertMs) && nowMs >= revertMs) {
      return {
        ...clone(),
        variant: schedule.nextVariant ?? "launch",
        schedule: null,
      };
    }
  }

  return {
    ...clone(),
    variant: schedule.variant,
  };
}

export function normalizeLandingConfig(raw: unknown): LandingConfigDocument {
  if (!raw || typeof raw !== "object") return DEFAULT_LANDING_CONFIG;
  const data = raw as Partial<LandingConfigDocument>;
  const variant = isLandingVariant(data.variant) ? data.variant : "launch";
  const availability = data.storeAvailability ?? DEFAULT_STORE_AVAILABILITY;
  return {
    variant,
    sections: {
      features: Boolean(data.sections?.features),
      pricing: Boolean(data.sections?.pricing),
      partners:
        data.sections?.partners === undefined
          ? true
          : Boolean(data.sections.partners),
    },
    hero: data.hero ?? {},
    utmCampaignKey:
      typeof data.utmCampaignKey === "string" ? data.utmCampaignKey : undefined,
    pricingSnapshot: data.pricingSnapshot ?? null,
    schedule: data.schedule ?? null,
    source: data.source ?? { kind: "default" },
    storeAvailability: {
      android: Array.isArray(availability.android)
        ? availability.android
            .map((code) => String(code).trim().toUpperCase())
            .filter((code) => /^[A-Z]{2}$/.test(code))
        : [...DEFAULT_STORE_AVAILABILITY.android],
      ios: Array.isArray(availability.ios)
        ? availability.ios
            .map((code) => String(code).trim().toUpperCase())
            .filter((code) => /^[A-Z]{2}$/.test(code))
        : [...DEFAULT_STORE_AVAILABILITY.ios],
    },
    founderOffer: normalizeFounderOffer(data.founderOffer),
    updatedBy: data.updatedBy,
    updatedAt:
      typeof data.updatedAt === "string"
        ? data.updatedAt
        : DEFAULT_LANDING_CONFIG.updatedAt,
  };
}

/**
 * One-shot Firestore fetch. Caller must not poll — scale-to-zero friendly.
 * Returns status so UI can keep strategy bootstrap until a real failure,
 * then apply DEFAULT_LANDING_CONFIG (launch) only as fallback.
 * Dynamic import avoids a static cycle with landing-config-firestore.
 */
export async function fetchLandingConfig(
  signal?: AbortSignal,
): Promise<LandingConfigFetchResult> {
  const { fetchLandingConfigFromFirestore } = await import(
    "@/lib/landing-config-firestore"
  );
  return fetchLandingConfigFromFirestore(signal);
}

/** Read `?preview_variant=` from the URL for admin preview (no config write). */
export function readPreviewVariant(
  search: string | undefined,
): LandingVariant | null {
  if (!search) return null;
  try {
    const params = new URLSearchParams(
      search.startsWith("?") ? search : `?${search}`,
    );
    const raw = params.get("preview_variant");
    if (raw === "launch") return "launch";
    // waitlist/beta preview is retired
    return null;
  } catch {
    return null;
  }
}
